/**
 * ProductsPage Component
 * Shows all products with sorting and pagination
 */

import { useEffect, useState, useRef } from 'react';
import { motion } from 'motion/react';
import { Layout } from '~/components/layout/Layout';
import { generateStaticPageMetaTags, applyMetaTags } from '~/lib/seo/meta-tags';
import { ProductGrid } from '~/components/product/ProductGrid';
import { getProducts } from '~/lib/shopify/api';
import { useServerData } from '~/lib/server-data';
import type { Product, PageInfo, SortKey } from '~/lib/shopify/types';
import { trackViewItemList } from '~/lib/tracking/gtm';

const sortOptions: Array<{ label: string; value: SortKey; reverse: boolean }> = [
  { label: 'Best Selling', value: 'BEST_SELLING', reverse: false },
  { label: 'Newest', value: 'CREATED', reverse: true },
  { label: 'Price: Low to High', value: 'PRICE', reverse: false },
  { label: 'Price: High to Low', value: 'PRICE', reverse: true },
  { label: 'Alphabetical', value: 'TITLE', reverse: false },
];

export function ProductsPage() {
  const serverData = useServerData();
  const [products, setProducts] = useState<Product[]>(serverData.products || []);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(serverData.productsPageInfo || null);
  const [isLoading, setIsLoading] = useState(!serverData.products);
  const [sortIndex, setSortIndex] = useState(0);
  const usedServerData = useRef(!!serverData.products);

  const currentSort = sortOptions[sortIndex];

  // Fetch products
  useEffect(() => {
    // Skip first fetch if we used server data (default sort only)
    if (usedServerData.current && sortIndex === 0) {
      usedServerData.current = false;
      return;
    }

    async function fetchProducts() {
      setIsLoading(true);

      try {
        const data = await getProducts({
          first: 24,
          sortKey: currentSort.value,
          reverse: currentSort.reverse,
        });

        setProducts(data.products);
        setPageInfo(data.pageInfo);
        trackViewItemList(data.products, 'All Headsets');
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchProducts();
  }, [currentSort]);

  // Update SEO
  useEffect(() => {
    const meta = generateStaticPageMetaTags('products', 'Browse our full range of professional headsets from Jabra, Poly, Axtel and more. Free next-day delivery on orders over £75.');
    applyMetaTags({ ...meta, title: 'All Headsets | IT Remarketing' });
  }, []);

  // Load more products
  const loadMore = async () => {
    if (!pageInfo?.hasNextPage || isLoading) return;

    setIsLoading(true);
    try {
      const data = await getProducts({
        first: 24,
        after: pageInfo.endCursor || undefined,
        sortKey: currentSort.value,
        reverse: currentSort.reverse,
      });

      setProducts((prev) => [...prev, ...data.products]);
      setPageInfo(data.pageInfo);
    } catch (err) {
      console.error('Failed to load more products:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-wide py-8 lg:py-12">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumbs */}
          <nav className="mb-4">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <a href="/" className="text-accent-silver hover:text-accent-white transition-colors">
                  Home
                </a>
              </li>
              <li className="text-accent-silver">/</li>
              <li className="text-accent-white">All Headsets</li>
            </ol>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="heading-lg text-accent-white mb-2">All Headsets</h1>
              <p className="text-accent-silver">
                Browse our full range of professional headsets
              </p>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-accent-silver">Sort by:</span>
              <select
                value={sortIndex}
                onChange={(e) => setSortIndex(Number(e.target.value))}
                className="bg-surface-coal border border-surface-slate rounded-lg px-3 py-2 text-sm text-accent-white focus:outline-none focus:border-brand-orange"
              >
                {sortOptions.map((option, index) => (
                  <option key={`${option.value}-${option.reverse}`} value={index}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {isLoading && products.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="product-card skeleton">
                <div className="aspect-square bg-surface-slate rounded-xl mb-4" />
                <div className="h-4 bg-surface-slate rounded w-1/3 mb-2" />
                <div className="h-5 bg-surface-slate rounded w-3/4 mb-2" />
                <div className="h-6 bg-surface-slate rounded w-1/4" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 bg-surface-coal rounded-2xl flex items-center justify-center">
              <svg className="w-10 h-10 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h2 className="text-xl font-display font-bold text-accent-white mb-2">
              No Products Found
            </h2>
            <p className="text-accent-silver">
              Check back soon for new products.
            </p>
          </div>
        ) : (
          <>
            <ProductGrid products={products} listName="All Products" />

            {/* Load More */}
            {pageInfo?.hasNextPage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center mt-12"
              >
                <button
                  onClick={loadMore}
                  disabled={isLoading}
                  className="btn-secondary"
                >
                  {isLoading ? 'Loading...' : 'Load More Products'}
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
